import React, { useState } from 'react';
import {
    Search, Filter, Shield, AlertTriangle, CreditCard,
    X, Lock, ImageIcon, Plus, Trash2, IdCard
} from 'lucide-react';

// User Detail & Edit Modal
const UserDetailModal = ({ user, onClose, onSave }) => {
    const [formData, setFormData] = useState(JSON.parse(JSON.stringify(user))); // Deep copy for editing
    const [activeTab, setActiveTab] = useState('basic'); // basic | bank

    // Toggle Blacklist
    const toggleBlacklist = () => {
        setFormData(prev => ({
            ...prev,
            riskStatus: prev.riskStatus === '正常' ? '黑名单' : '正常',
            blacklistReason: prev.riskStatus === '正常' ? '' : '' // Clear reason if removing from blacklist
        }));
    };

    // Bank Card Operations
    const addBankCard = () => {
        const newCard = { id: `C${Date.now()}`, bank: '', account: '', branch: '', status: 'normal' };
        setFormData(prev => ({ ...prev, bankCards: [...prev.bankCards, newCard] }));
    };

    const removeBankCard = (cardId) => {
        setFormData(prev => ({ ...prev, bankCards: prev.bankCards.filter(c => c.id !== cardId) }));
    };

    const updateBankCard = (cardId, field, value) => {
        setFormData(prev => ({
            ...prev,
            bankCards: prev.bankCards.map(c => c.id === cardId ? { ...c, [field]: value } : c)
        }));
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] animate-in fade-in zoom-in duration-200">
            <div className="bg-white rounded-xl shadow-xl w-[700px] p-0 overflow-hidden flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-10">
                    <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold text-white shadow-md ${formData.riskStatus === '黑名单' ? 'bg-slate-800' : 'bg-blue-600'}`}>
                            {formData.name[0]}
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                {formData.name}
                                {formData.riskStatus === '黑名单' && (
                                    <span className="bg-red-100 text-red-600 border border-red-200 px-2 py-0.5 rounded text-xs flex items-center gap-1">
                                        <AlertTriangle size={12} fill="currentColor" /> 黑名单
                                    </span>
                                )}
                            </h3>
                            <p className="text-xs text-slate-500 mt-1 font-mono">ID: {formData.id}</p>
                        </div>
                    </div>
                    <button onClick={onClose}><X size={20} className="text-slate-400 hover:text-slate-600" /></button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto space-y-6 flex-1">
                    
                    {/* Tabs */}
                    <div className="flex gap-4 border-b border-slate-100 mb-2">
                        <button onClick={() => setActiveTab('basic')} className={`pb-2 text-sm font-medium transition-colors relative ${activeTab === 'basic' ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}>
                            基础信息 & 风控
                            {activeTab === 'basic' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 rounded-t-full"></div>}
                        </button>
                        <button onClick={() => setActiveTab('bank')} className={`pb-2 text-sm font-medium transition-colors relative ${activeTab === 'bank' ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}>
                            银行卡信息 ({formData.bankCards.length})
                            {activeTab === 'bank' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 rounded-t-full"></div>}
                        </button>
                    </div>

                    {activeTab === 'basic' && (
                        <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
                            {/* Basic Fields */}
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-xs font-medium text-slate-500 mb-1.5">手机号码 <span className="text-red-500">*</span></label>
                                    <input 
                                        type="tel" 
                                        value={formData.phone} 
                                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-100 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-slate-500 mb-1.5">身份证号 (实名认证) <Lock size={10} className="inline"/></label>
                                    <input 
                                        type="text" 
                                        value={formData.idCard} 
                                        disabled // Usually ID is not editable after auth
                                        className="w-full border border-slate-100 bg-slate-50 rounded-lg px-3 py-2 text-sm text-slate-500 cursor-not-allowed"
                                    />
                                </div>
                            </div>

                            {/* ID Card Photos */}
                            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                                <div className="flex justify-between items-center mb-3">
                                    <h4 className="text-xs font-bold text-slate-700 flex items-center gap-2"><IdCard size={14}/> 实名认证证件</h4>
                                    <button
                                        onClick={() => {
                                            setFormData({
                                                ...formData,
                                                idCard: '',
                                                authStatus: '待认证'
                                            });
                                        }}
                                        className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600 hover:bg-red-50 px-2 py-1 rounded transition-colors"
                                    >
                                        <Trash2 size={12}/> 清空实名认证
                                    </button>
                                </div>
                                <div className="flex gap-4">
                                    <div className="w-1/2 aspect-[1.6] bg-slate-200 rounded-lg flex flex-col items-center justify-center text-slate-400 border-2 border-dashed border-slate-300 hover:bg-slate-100 transition-colors cursor-pointer">
                                        <ImageIcon size={24} className="mb-2"/>
                                        <span className="text-xs">身份证人像面</span>
                                    </div>
                                    <div className="w-1/2 aspect-[1.6] bg-slate-200 rounded-lg flex flex-col items-center justify-center text-slate-400 border-2 border-dashed border-slate-300 hover:bg-slate-100 transition-colors cursor-pointer">
                                        <ImageIcon size={24} className="mb-2"/>
                                        <span className="text-xs">身份证国徽面</span>
                                    </div>
                                </div>
                            </div>

                            {/* Risk Control */}
                            <div className={`p-4 rounded-xl border transition-colors ${formData.riskStatus === '黑名单' ? 'bg-red-50 border-red-100' : 'bg-green-50 border-green-100'}`}>
                                <div className="flex justify-between items-center mb-3">
                                    <h4 className={`text-sm font-bold flex items-center gap-2 ${formData.riskStatus === '黑名单' ? 'text-red-700' : 'text-green-700'}`}>
                                        <Shield size={16}/> 
                                        {formData.riskStatus === '黑名单' ? '当前状态：黑名单 (禁止交易)' : '当前状态：正常 (允许交易)'}
                                    </h4>
                                    <button 
                                        onClick={toggleBlacklist}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border shadow-sm transition-all ${formData.riskStatus === '黑名单' ? 'bg-white text-green-600 border-green-200 hover:bg-green-50' : 'bg-white text-red-600 border-red-200 hover:bg-red-50'}`}
                                    >
                                        {formData.riskStatus === '黑名单' ? '解除黑名单' : '加入黑名单'}
                                    </button>
                                </div>
                                {formData.riskStatus === '黑名单' && (
                                    <div>
                                        <label className="block text-xs font-medium text-red-600 mb-1.5">拉黑原因说明</label>
                                        <textarea 
                                            value={formData.blacklistReason}
                                            onChange={(e) => setFormData({...formData, blacklistReason: e.target.value})}
                                            className="w-full border border-red-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-red-100 outline-none text-red-800 placeholder:text-red-300 h-20"
                                            placeholder="请输入将其列入黑名单的原因（例如：多次违约、掺假等）..."
                                        ></textarea>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'bank' && (
                        <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-slate-500">已绑定 {formData.bankCards.length} 张银行卡</span>
                                <button onClick={addBankCard} className="flex items-center gap-1 text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors shadow-sm">
                                    <Plus size={14}/> 新增银行卡
                                </button>
                            </div>
                            
                            {formData.bankCards.map((card) => (
                                <div key={card.id} className="p-4 border border-slate-200 rounded-xl bg-slate-50 hover:border-blue-300 transition-all group relative">
                                    <button 
                                        onClick={() => removeBankCard(card.id)}
                                        className="absolute top-2 right-2 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                                        title="删除此卡"
                                    >
                                        <Trash2 size={16}/>
                                    </button>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs text-slate-500 mb-1">开户银行 <span className="text-red-500">*</span></label>
                                            <input
                                                type="text"
                                                value={card.bank}
                                                onChange={(e) => updateBankCard(card.id, 'bank', e.target.value)}
                                                placeholder="例如：中国工商银行"
                                                className="w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-sm focus:border-blue-400 outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-slate-500 mb-1">开户支行 <span className="text-red-500">*</span></label>
                                            <input
                                                type="text"
                                                value={card.branch}
                                                onChange={(e) => updateBankCard(card.id, 'branch', e.target.value)}
                                                placeholder="例如：上海徐汇支行"
                                                className="w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-sm focus:border-blue-400 outline-none"
                                            />
                                        </div>
                                        <div className="col-span-2">
                                            <label className="block text-xs text-slate-500 mb-1">银行卡号 <span className="text-red-500">*</span></label>
                                            <input
                                                type="text"
                                                value={card.account}
                                                onChange={(e) => updateBankCard(card.id, 'account', e.target.value)}
                                                placeholder="请输入银行卡号"
                                                className="w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-sm font-mono tracking-wide focus:border-blue-400 outline-none"
                                            />
                                        </div>
                                        <div className="col-span-2">
                                            <label className="block text-xs text-slate-500 mb-1.5">银行卡照片</label>
                                            <div className="flex gap-3">
                                                <div className="w-40 h-24 bg-slate-100 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:bg-slate-50 hover:border-blue-300 transition-colors cursor-pointer">
                                                    <ImageIcon size={20} className="mb-1"/>
                                                    <span className="text-xs">卡正面</span>
                                                </div>
                                                <div className="flex-1 text-xs text-slate-400 flex flex-col justify-center">
                                                    <p>请上传银行卡正面照片</p>
                                                    <p className="text-slate-300 mt-1">支持 jpg、png 格式</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            
                            {formData.bankCards.length === 0 && (
                                <div className="text-center py-8 text-slate-400 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50">
                                    <CreditCard size={32} className="mx-auto mb-2 opacity-50"/>
                                    <p className="text-sm">暂无银行卡信息，请点击右上角添加</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-slate-100 bg-white flex justify-end gap-3">
                    <button onClick={onClose} className="px-6 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm hover:bg-slate-200">取消</button>
                    <button onClick={() => onSave(formData)} className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 shadow-lg shadow-blue-200">保存更改</button>
                </div>
            </div>
        </div>
    );
};

const UsersView = ({ users, onUpdateUser }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    // 筛选逻辑
    const filteredUsers = users ? users.filter(u => 
        u.name.includes(searchTerm) || 
        u.phone.includes(searchTerm) || 
        u.idCard.includes(searchTerm)
    ) : [];

    // Mock Pagination Logic
    const totalItems = filteredUsers.length;
    const totalPages = Math.ceil(totalItems / pageSize);
    const currentData = filteredUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const handleSaveUser = (updatedUser) => {
        onUpdateUser(updatedUser);
        setShowEditModal(false);
        setSelectedUser(null);
    };

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 flex flex-col h-[calc(100vh-140px)]">
                {/* Header */}
                <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                    <div>
                        <h3 className="font-bold text-slate-800 text-lg">客商管理 (自然人)</h3>
                        <p className="text-xs text-slate-500 mt-1">共 {totalItems} 位注册供应商</p>
                    </div>
                    <div className="flex gap-3">
                         <div className="relative">
                             <Search className="absolute left-3 top-2.5 text-slate-400 w-4 h-4" />
                             <input 
                                type="text" 
                                placeholder="搜索姓名/手机号/身份证..." 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm w-64 focus:ring-2 focus:ring-blue-100 outline-none"
                             />
                         </div>
                         <button className="flex items-center gap-2 bg-white border border-slate-200 text-slate-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-50">
                            <Filter size={16} /> 筛选
                         </button>
                    </div>
                </div>
                
                {/* Table Content */}
                <div className="flex-1 overflow-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 text-slate-500 font-medium sticky top-0 z-10">
                            <tr>
                                <th className="p-4 pl-6 w-64">客商信息</th>
                                <th className="p-4 w-48">联系方式</th>
                                <th className="p-4 w-48">身份证号</th>
                                <th className="p-4 w-32">实名状态</th>
                                <th className="p-4 w-32">风控状态</th>
                                <th className="p-4 w-32">银行卡</th>
                                <th className="p-4 text-right pr-6">操作</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {currentData.map(user => (
                                <tr key={user.id} className="hover:bg-slate-50 transition-colors group">
                                    <td className="p-4 pl-6">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm text-white ${user.riskStatus === '黑名单' ? 'bg-slate-600' : 'bg-blue-600'}`}>
                                                {user.name[0]}
                                            </div>
                                            <div>
                                                <div className="font-bold text-slate-800">{user.name}</div>
                                                <div className="text-xs text-slate-400 font-mono">{user.id}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-4 text-slate-600 font-mono">{user.phone}</td>
                                    <td className="p-4 text-slate-600 font-mono">{user.idCard}</td>
                                    <td className="p-4">
                                        <span className="bg-green-50 text-green-600 px-2 py-1 rounded text-xs border border-green-100">
                                            已认证
                                        </span>
                                    </td>
                                    <td className="p-4">
                                        <span className={`px-2 py-1 rounded text-xs border flex items-center gap-1 w-fit ${user.riskStatus === '黑名单' ? 'bg-red-50 text-red-600 border-red-100' : 'bg-blue-50 text-blue-600 border-blue-100'}`}>
                                            {user.riskStatus === '黑名单' && <AlertTriangle size={10} />}
                                            {user.riskStatus}
                                        </span>
                                    </td>
                                    <td className="p-4">
                                        <div className="flex items-center gap-1 text-slate-500">
                                            <CreditCard size={14} />
                                            {user.bankCards.length} 张
                                        </div>
                                    </td>
                                    <td className="p-4 text-right pr-6">
                                        <button 
                                            onClick={() => { setSelectedUser(user); setShowEditModal(true); }}
                                            className="text-blue-600 hover:text-blue-800 font-medium text-xs px-3 py-1.5 rounded hover:bg-blue-50 transition-colors"
                                        >
                                            管理
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Footer Pagination */}
                <div className="p-4 border-t border-slate-100 flex justify-between items-center bg-white">
                    <span className="text-xs text-slate-500">
                        显示 {((currentPage - 1) * pageSize) + 1} 到 {Math.min(currentPage * pageSize, totalItems)} 条，共 {totalItems} 条
                    </span>
                    <div className="flex gap-2">
                        <button 
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(p => p - 1)}
                            className="px-3 py-1 border border-slate-200 rounded text-xs hover:bg-slate-50 disabled:opacity-50"
                        >
                            上一页
                        </button>
                        <button 
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(p => p + 1)}
                            className="px-3 py-1 border border-slate-200 rounded text-xs hover:bg-slate-50 disabled:opacity-50"
                        >
                            下一页
                        </button>
                    </div>
                </div>
            </div>

            {/* Edit Modal */}
            {showEditModal && selectedUser && (
                <UserDetailModal 
                    user={selectedUser} 
                    onClose={() => { setShowEditModal(false); setSelectedUser(null); }}
                    onSave={handleSaveUser}
                />
            )}
        </div>
    );
};

export default UsersView;